import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Fetch tasks for a specific case
 * GET /api/cases/{caseId}/tasks
 */
export async function fetchCaseTasks(caseId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/tasks`);
    if (!response.ok) {
        logError(`Failed to fetch tasks for case ${caseId}`, response.status);
        throw new Error('Failed to fetch tasks');
    }
    return response.json();
}

/**
 * Create a new task
 * POST /api/cases/{caseId}/tasks
 */
export async function createTask(caseId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError(`Failed to create task for case ${caseId}`, response.status);
        const error = new Error('Failed to create task');
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
    }
    return response.json();
}

/**
 * Update a task (status, assign, date, etc)
 * PATCH /api/cases/{caseId}/tasks/{taskId}
 */
export async function updateTask(caseId, taskId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError(`Failed to update task ${taskId}`, response.status);
        throw new Error('Failed to update task');
    }
    return response.json();
}

/**
 * Delete a task
 * DELETE /api/cases/{caseId}/tasks/{taskId}
 */
export async function deleteTask(caseId, taskId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/tasks/${taskId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError(`Failed to delete task ${taskId}`, response.status);
        throw new Error('Failed to delete task');
    }
    // DELETE often returns 204 No Content with no body
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return { success: true };
    }
    // Try to parse JSON if there's a response body
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Fetch comments for a task
 * GET /api/tasks/{taskId}/comments
 */
export async function fetchTaskComments(taskId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/tasks/${taskId}/comments`);
    if (!response.ok) {
        logError(`Failed to fetch comments for task ${taskId}`, response.status);
        throw new Error('Failed to fetch task comments');
    }
    return response.json();
}

/**
 * Add a comment to a task
 * POST /api/tasks/{taskId}/comments
 */
export async function addTaskComment(taskId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/tasks/${taskId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError(`Failed to add comment to task ${taskId}`, response.status);
        throw new Error('Failed to add comment');
    }
    return response.json();
}

/**
 * Fetch all tasks across all cases (Executive View)
 * Loop through paginated results to get comprehensive list for Stats
 * GET /api/tasks
 */
export async function fetchAllTasks() {
    let allTasks = [];
    let cursor = "0";
    let hasMore = true;
    const SAFETY_LIMIT = 50; // Fetch up to ~2500 tasks

    let pageCount = 0;
    while (hasMore && pageCount < SAFETY_LIMIT) {
        const query = new URLSearchParams({
            sort: 'dueDate',
            order: 'asc',
            limit: '50',
            cursor: cursor || '0'
        });

        const url = `${BASE_URL}/api/tasks?${query.toString()}`;

        const response = await fetchWithAuth(url);

        // Graceful fallback
        if (response.status === 404) return allTasks;

        if (!response.ok) {
            logError('Failed to fetch tasks page', response.status);
            throw new Error('Failed to fetch global tasks');
        }

        const data = await response.json();

        if (Array.isArray(data)) {
            if (data.length === 0) {
                hasMore = false;
            } else {
                allTasks = [...allTasks, ...data];
            }
        } else {
            // Handle wrappers
            if (data.tasks && Array.isArray(data.tasks)) allTasks = [...allTasks, ...data.tasks];
            else if (data.data && Array.isArray(data.data)) allTasks = [...allTasks, ...data.data];
        }

        const nextCursor = response.headers.get('X-Next-Cursor');
        if (nextCursor && nextCursor !== 'null' && nextCursor !== 'undefined') {
            cursor = nextCursor;
        } else {
            hasMore = false;
        }
        pageCount++;
    }

    return allTasks;
}

export async function fetchTasksPage(cursor) {
    // Helper if needed, but fetchAllTasks covers main use case
    // We can keep this stub or remove. 
    // For now, I'll remove the specific export to avoid confusion or keep it internal.
    // Actually, TasksDashboard might still import it if I don't update it immediately.
    // I will update TasksDashboard next.
    return { tasks: [], nextCursor: null };
}
